import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Truck, Heart, Star, Quote } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { LightningBolt, CheetahCorner, CheetahDivider, StarSpark } from "../components/BrandAccents";
import { StarRating } from "../components/StarRating";

const HERO_BANNER = "https://customer-assets.emergentagent.com/job_neon-boutique-test/artifacts/nt5rqww5_Untitled%20design.png";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/home/sections").then(({ data }) => setSections(data)).catch(() => {});
    api.get("/categories/with-preview").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const productsByBadge = (badge, limit) => products.filter((p) => p.badge === badge).slice(0, limit ?? 4);
  const productsByIds = (ids, limit) => {
    if (!ids?.length) return [];
    const map = Object.fromEntries(products.map((p) => [p.id, p]));
    return ids.map((id) => map[id]).filter(Boolean).slice(0, limit ?? ids.length);
  };

  const renderSection = (s) => {
    switch (s.type) {
      case "hero":
        return (
          <section key={s.id} className="relative overflow-hidden">
            <div className="w-full mx-auto max-w-[1100px] aspect-[3/1] overflow-hidden">
              <img src={s.config?.image_url || HERO_BANNER} alt={s.title || "Snip Happens Boutique"} className="w-full h-full object-cover object-center block"/>
            </div>
          </section>
        );
      case "marquee":
        return (
          <section key={s.id} className="border-y border-[#e5dccd] bg-[#1c1815] text-white overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap py-3.5">
              {Array(2).fill(0).map((_, k) => (
                <div key={k} className="flex items-center gap-7 px-3.5 font-bold text-[11px] uppercase tracking-[0.25em] text-white/85">
                  <StarSpark size={11} color="#2eb6a8"/> Free Shipping Over $50
                  <StarSpark size={11} color="#e8478e"/> Instant Digital Downloads
                  <StarSpark size={11} color="#9c6dde"/> Trendy Designs
                  <StarSpark size={11} color="#2eb6a8"/> Boutique Finds
                  <StarSpark size={11} color="#e8478e"/> You.
                  <StarSpark size={11} color="#2eb6a8"/> Free Shipping Over $50
                  <StarSpark size={11} color="#e8478e"/> Instant Digital Downloads
                  <StarSpark size={11} color="#9c6dde"/> Trendy Designs
                  <StarSpark size={11} color="#2eb6a8"/> Boutique Finds
                  <StarSpark size={11} color="#e8478e"/> You.
                </div>
              ))}
            </div>
          </section>
        );
      case "categories":
        return (
          <section key={s.id} className="max-w-7xl mx-auto px-6 py-12 sm:py-16 relative">
            <div className="flex items-end justify-between mb-7">
              <div>
                {s.subtitle && <div className="font-script text-2xl sm:text-3xl text-[#e8478e]">{s.subtitle}</div>}
                <h2 className="font-display text-3xl sm:text-5xl font-black text-[#1c1815] tracking-tight">{s.title || "Categories"}</h2>
              </div>
              <Link to="/shop" className="hidden sm:inline text-xs font-bold uppercase tracking-[0.2em] text-[#2eb6a8] hover:text-[#1f8d83] link-fancy">View All →</Link>
            </div>
            <div className="max-w-2xl space-y-2 sm:space-y-3">
              {categories.length === 0 ? (
                <div className="text-center text-sm text-[#8a8076] py-8">No categories yet — add some in Admin → Categories.</div>
              ) : categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/shop?category=${c.slug}`}
                  data-testid={`category-${c.slug}`}
                  className="group flex items-center gap-4 p-3 sm:p-4 rounded-2xl bg-white border border-[#e5dccd] hover:border-[#2eb6a8] shadow-sm hover:shadow-md hover:translate-x-1 transition-all"
                >
                  <div
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-2 transition-all bg-[#faf4e9] flex items-center justify-center flex-shrink-0"
                    style={{ borderColor: c.accent_color }}
                  >
                    {(c.preview_image || c.image_url) ? (
                      <img src={c.preview_image || c.image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
                    ) : (
                      <span className="text-xl font-display font-black" style={{ color: c.accent_color }}>{c.name.slice(0, 1)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg sm:text-xl font-extrabold text-[#1c1815] group-hover:text-[#e8478e] transition-colors truncate">
                      {c.name}
                    </div>
                    {typeof c.product_count === "number" && c.product_count > 0 && (
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8076] font-bold mt-0.5">
                        {c.product_count} {c.product_count === 1 ? "item" : "items"}
                      </div>
                    )}
                  </div>
                  <ArrowRight size={18} className="text-[#8a8076] group-hover:text-[#e8478e] group-hover:translate-x-1 transition-all flex-shrink-0"/>
                </Link>
              ))}
            </div>
            <div className="sm:hidden mt-6 text-center">
              <Link to="/shop" className="text-xs font-bold uppercase tracking-[0.2em] text-[#2eb6a8] hover:text-[#1f8d83]">View All →</Link>
            </div>
          </section>
        );
      case "best_sellers":
      case "trending":
      case "featured":
      case "custom_grid": {
        const limit = s.config?.limit ?? (s.type === "featured" ? 8 : 4);
        let items;
        if (s.type === "best_sellers") items = productsByBadge("bestseller", limit);
        else if (s.type === "trending") items = productsByBadge("trending", limit);
        else if (s.type === "custom_grid") items = productsByIds(s.config?.product_ids, limit);
        else items = products.slice(0, limit);
        if (items.length === 0) return null;
        const accent = s.type === "best_sellers" ? "#2eb6a8" : s.type === "trending" ? "#e8478e" : "#9c6dde";
        const showLightning = s.type === "trending";
        return (
          <section key={s.id} className="max-w-7xl mx-auto px-6 py-20 sm:py-24">
            <div className="flex items-end justify-between mb-10">
              <div>
                {s.subtitle && <div className="font-script text-3xl flex items-center gap-3" style={{ color: accent }}>{s.subtitle} {showLightning && <LightningBolt size={20}/>}</div>}
                <h2 className="font-display text-4xl sm:text-5xl font-black text-[#1c1815] tracking-tight">{s.title}</h2>
              </div>
              {s.type !== "featured" && (
                <Link to="/shop" className="text-xs font-bold uppercase tracking-[0.2em] hover:text-[#c93875] link-fancy" style={{ color: "#e8478e" }}>All →</Link>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {items.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
            </div>
          </section>
        );
      }
      case "cta":
        return (
          <section key={s.id} className="max-w-7xl mx-auto px-6 pb-24">
            <div className="relative overflow-hidden rounded-3xl border border-[#e5dccd] bg-white p-10 sm:p-16 shadow-sm grunge-overlay">
              <CheetahCorner position="top-right" size={150} opacity={0.18}/>
              <CheetahCorner position="bottom-left" size={120} opacity={0.15}/>
              <div className="absolute top-8 right-12 hidden sm:block opacity-80"><LightningBolt size={36}/></div>
              <div className="relative max-w-2xl">
                {s.subtitle && <div className="font-script text-3xl text-[#2eb6a8] mb-2">{s.subtitle}</div>}
                <h2 className="font-display text-4xl sm:text-6xl font-black text-[#1c1815] tracking-tight leading-[1.05]">
                  {s.title || "Designs That Don't Whisper."}
                </h2>
                {s.config?.body && <p className="mt-5 text-[#4a4239] text-base leading-relaxed">{s.config.body}</p>}
                <Link to={s.config?.button_link || "/shop"} data-testid="cta-shop"
                  className="mt-8 inline-flex items-center gap-2 bg-[#1c1815] text-white font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 rounded-full hover:bg-[#2eb6a8] hover:-translate-y-0.5 hover:shadow-lg transition-all">
                  {s.config?.button_label || "Shop Everything"} <ArrowRight size={14}/>
                </Link>
              </div>
            </div>
          </section>
        );
      case "testimonials":
        return <TestimonialsSection key={s.id} section={s}/>;
      default:
        return null;
    }
  };

  return (
    <div data-testid="home-page" className="paper-warm">
      {sections.map((s, i) => (
        <div key={s.id}>
          {renderSection(s)}
          {i === 2 && <CheetahDivider className="max-w-5xl mx-auto"/>}
        </div>
      ))}
    </div>
  );
}

function TestimonialsSection({ section }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const limit = section.config?.limit || 6;
    api.get("/reviews/featured", { params: { limit, min_rating: 5 } })
      .then(({ data }) => setReviews(data))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, [section.config?.limit]);

  if (loading) return null;
  if (reviews.length === 0) return null;

  return (
    <section data-testid="home-testimonials" className="max-w-7xl mx-auto px-6 py-16 sm:py-20 relative">
      <div className="text-center mb-10">
        {section.subtitle && <div className="font-script text-3xl text-[#e8478e]">{section.subtitle}</div>}
        <h2 className="font-display text-4xl sm:text-5xl font-black text-[#1c1815] tracking-tight">{section.title || "What Our Babes Are Saying"}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {reviews.map((r) => (
          <Link
            key={r.id}
            to={`/product/${r.product.slug}`}
            data-testid={`testimonial-${r.id}`}
            className="group relative p-6 rounded-3xl bg-white border border-[#e5dccd] hover:border-[#e8478e] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col"
          >
            <Quote size={28} className="text-[#e8478e] opacity-30 mb-2"/>
            <StarRating value={r.rating} size={16}/>
            {r.title && <div className="mt-3 font-display text-lg font-extrabold text-[#1c1815] leading-snug">{r.title}</div>}
            <p className="mt-2 text-[#4a4239] text-sm leading-relaxed line-clamp-4 flex-1">{r.body}</p>
            <div className="mt-5 pt-4 border-t border-[#e5dccd] flex items-center gap-3">
              <img src={r.product.image_url} alt={r.product.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0"/>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8076] font-bold">{r.user_name}</div>
                <div className="text-sm font-bold text-[#1c1815] truncate group-hover:text-[#e8478e] transition-colors">{r.product.title}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

